import React, { FunctionComponent, useState } from 'react'
import { connect, ConnectedProps, useSelector } from 'react-redux'

import { cloneDeep, noop } from 'lodash-es'

import { AssignmentStatuses } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import { getEffectiveRecipientLyraId } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import {
  AssignmentResponse,
  ExerciseDraftPayload,
} from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Episode, EpisodeContents } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import {
  ACTIVITY_EXIT_MODAL_CONTENT,
  ActivityExitModalContent,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/activityExitModalContent/ActivityExitModalContent'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { ActivityEntry } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/activityEntry/ActivityEntry'

import { getAssignmentProviderPayload } from './data/assignmentPayload'
import {
  useSetAssignmentDraftMutation,
  useSetAssignmentDraftResponseToAssignmentMutation,
  useSetAssignmentMutation,
  useSetAssignmentResponseMutation,
  useUpdateAssignmentResponseMutation,
} from './data/assignmentsApi'
import { clearResponseDetails, setResponseDetails } from './data/assignmentsSlice'
import { getAuthUser } from '../../data/auth/authSelectors'
import { getClientCurrentSessionCount } from '../../data/lyraTherapy/clientSelectors'
import { RootState, useAppDispatch } from '../../data/store'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { getLTVideoSessionOpen } from '../data/ltVideoSelectors'
import { useUpdateCurriculumMutation } from '../episodes/data/episodesApi'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

type ProviderExerciseDraftProps = ConnectedProps<typeof connector> & {
  assignmentId?: string
  content: any
  contentId?: string
  editMode?: boolean
  instructions: string
  name?: string
  onExit: () => void
  response?: Dict
  responseId?: string
  sessionCount?: number
  title?: string
  videoSessionOpen: boolean
  selectedEpisodeId?: string
  recipientLyraId?: string
}

const ProviderExerciseDraft: FunctionComponent<ProviderExerciseDraftProps> = ({
  assignmentId,
  content,
  contentId,
  currentSessionCount,
  editMode,
  instructions,
  name,
  onExit,
  response,
  responseId,
  sessionCount = 0,
  title,
  videoSessionOpen,
  selectedEpisodeId,
  recipientLyraId,
}) => {
  const dispatch = useAppDispatch()
  const [updateCurriculum] = useUpdateCurriculumMutation()
  const [setAssignment] = useSetAssignmentMutation()
  const [setAssignmentResponse] = useSetAssignmentResponseMutation()
  const [updateAssignmentResponse] = useUpdateAssignmentResponseMutation()
  const [setAssignmentDraft] = useSetAssignmentDraftMutation()
  const [setAssignmentDraftResponseToAssignment] = useSetAssignmentDraftResponseToAssignmentMutation()
  const user = useSelector(getAuthUser)
  const [exitModalOpen, setExitModalOpen] = useState<boolean>(false)
  const [exitModalContent, setExitModalContent] = useState<React.ReactElement | null>(null)
  const { data: clientDetails } = useSelectedClientData()
  const { data: clientDetailsSelectedEpisode } = useSelectedEpisodeForClient(selectedEpisodeId) as {
    data: Episode | undefined
  }
  const episodeId = clientDetailsSelectedEpisode?.id || ''
  const assignmentProviderPayload = getAssignmentProviderPayload(clientDetailsSelectedEpisode, user?.id ?? '')
  const curriculum = clientDetailsSelectedEpisode?.curriculum || []
  const teenRecipientLyraId = clientDetailsSelectedEpisode?.patient_id

  const saveExerciseDraft = (formValues: Dict) => {
    if (!assignmentProviderPayload) return

    // It's possible to be in edit mode without a Provider draft so we want to set a new response if that is the case
    const isNewDraft = !response
    const data = {
      assignment_id: assignmentId || null,
      id: responseId || null,
      provider_started: true,
      response: formValues,
      status: AssignmentStatuses.provider_draft,
    }

    if (editMode && assignmentId) {
      if (!isNewDraft) {
        updateAssignmentResponse(data as AssignmentResponse).unwrap()
      } else {
        // Create a new draft when in 'edit' mode eg. The entry point was from clicking edit in the
        // dropdown for the assignment
        setAssignmentResponse(data).unwrap()
      }
      dispatch(setResponseDetails(data as AssignmentResponse))
    } else if (!editMode && assignmentId) {
      // Exercise belongs to a Session so create the new draft
      setAssignmentResponse(data).unwrap()
    } else {
      // Draft is for an Exercise that has not been added to a Session yet
      addDraftToAssignedExercise(data)
    }
    onExit()
  }

  const addDraftToAssignedExercise = (draft: ExerciseDraftPayload) => {
    if (!assignmentProviderPayload) return

    let providerDraftResponse: AssignmentResponse

    return setAssignmentDraft(draft)
      .unwrap()
      .then((draftResponse) => {
        providerDraftResponse = draftResponse
        dispatch(setResponseDetails(draftResponse))

        if (sessionCount > currentSessionCount && !editMode) {
          return setAssignment({
            ...assignmentProviderPayload,
            patient_id: clientDetails?.id as string,
            content_id: contentId as string,
            content_meta_data: content,
            instructions: instructions,
            session_period: sessionCount,
            recipient_id: recipientLyraId,
          })
            .unwrap()
            .then((assignment) => {
              setAssignmentDraftResponseToAssignment(
                Object.assign(cloneDeep(providerDraftResponse), {
                  assignment_id: assignment.id,
                }),
              )
                .unwrap()
                .then(() => {
                  const curriculumCopy = cloneDeep(curriculum)
                  const sessionToUpdate = curriculumCopy.findIndex(
                    (sessionObject) => sessionObject.session === sessionCount,
                  )

                  const contentIndex = curriculumCopy[sessionToUpdate].contents.findIndex(
                    (content: EpisodeContents) =>
                      content.name === name &&
                      getEffectiveRecipientLyraId(content.recipient_id, teenRecipientLyraId) ===
                        getEffectiveRecipientLyraId(recipientLyraId, teenRecipientLyraId),
                  )
                  curriculumCopy[sessionToUpdate].contents[contentIndex] = {
                    name: name as string,
                    title: title as string,
                    group: 'exercise',
                    content_type: 'form',
                    instructions: instructions,
                    content_meta_data: content,
                    id: assignment.id,
                    recipient_id: recipientLyraId,
                  }

                  return updateCurriculum({ id: episodeId!, curriculum: curriculumCopy }).unwrap()
                })
                .then(() => {
                  dispatch(clearResponseDetails())
                  onExit()
                })
            })
        } else {
          return
        }
      })
  }

  const onDraftExit = (formValues: Dict) => {
    setExitModalOpen(true)
    setExitModalContent(
      <ActivityExitModalContent
        exitModalContentType={ACTIVITY_EXIT_MODAL_CONTENT.CLOSE_NEW_PROVIDER_STARTED_DRAFT}
        onPrimaryButtonPress={() => {
          saveExerciseDraft(formValues.values as Dict)
        }}
        onRequestClose={() => {
          onExit()
        }}
        onTertiaryButtonPress={() => {
          onExit()
        }}
      />,
    )
  }

  return (
    <>
      <ActivityEntry
        contentMetadata={content}
        deleteDraft={noop}
        disableAutoSave
        exit={(values) => onDraftExit(values as Dict)}
        focused={false}
        instructions={instructions}
        name={name}
        saveForm={({ values }) => {
          saveExerciseDraft(values as Dict)
        }}
        showAllPages
        title={title}
        withPageBreaks={false}
        isProviderPreview={false}
        isProviderDraft
        videoSessionOpen={videoSessionOpen}
        response={response}
        disableIsRequiredFormValidation
      />

      <Modal
        modalContents={exitModalContent}
        onRequestClose={() => setExitModalOpen(false)}
        onCloseEnd={() => {}}
        visible={exitModalOpen}
      />
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    currentSessionCount: getClientCurrentSessionCount(state) || 0,
    videoSessionOpen: getLTVideoSessionOpen(state),
  }
}

const connector = connect(mapStateToProps, null)

export default connector(ProviderExerciseDraft)
